import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireLeadReason, type TireQuoteCustomerLeadRecord } from '@navio/nsdk';
import { NoddiBadge, NoddiButton, NoddiCard, NoddiDialog } from 'noddi-ui-web';
import { CustomDateFormats, noddiFormatDate } from 'noddi-util';
import { useState } from 'react';
import { ReadWhyButton } from './ReadWhyButton';
import { RiskList } from './RiskList';
import { StatusBand } from './StatusBand';
import {
  getSeverityLabel,
  getTireLeadReasonLabel,
  getTirePositionLabel,
  SEVERITY_BADGE_VARIANT
} from './tireLeadLabels';
import type { TireLeadReasonCopy } from './tireLeadReasonCopy';

type Props = {
  lead: TireQuoteCustomerLeadRecord;
  reason: TireLeadReason;
  copy: TireLeadReasonCopy;
  onOpenWhy: () => void;
  /** Missing when the customer can no longer set a reminder. */
  onOpenRemind?: () => void;
};

/** Any reason but worn tread: what the technician found per tire, with photos and their note. */
export const FindingsStatusCard = ({ lead, reason, copy, onOpenWhy, onOpenRemind }: Props) => (
  <NoddiCard>
    <div className='flex flex-col gap-5'>
      <Findings lead={lead} reason={reason} copy={copy} />
      <RiskList risks={copy.risks} severity={copy.severity} />
      {reason !== TireLeadReason.CUSTOMER_WISH && <ReadWhyButton onClick={onOpenWhy} />}
      {onOpenRemind && (
        <NoddiButton variant='outline' startIcon='Bell' className='w-full' onClick={onOpenRemind}>
          <Trans>Remind me later</Trans>
        </NoddiButton>
      )}
    </div>
  </NoddiCard>
);

/** The same status across the desktop page: the findings, then one column per point of the why sheet. */
export const FindingsStatusBand = ({ lead, reason, copy, onOpenWhy }: Omit<Props, 'onOpenRemind'>) => (
  <StatusBand
    points={copy.whyPoints.slice(0, 3).map((point) => ({ icon: 'TriangleAlert', ...point }))}
    severity={copy.severity}
  >
    <Findings lead={lead} reason={reason} copy={copy} />
    {reason !== TireLeadReason.CUSTOMER_WISH && <ReadWhyButton onClick={onOpenWhy} />}
  </StatusBand>
);

const Findings = ({ lead, reason, copy }: Omit<Props, 'onOpenWhy' | 'onOpenRemind'>) => {
  const [openPhotoUrl, setOpenPhotoUrl] = useState<string | null>(null);
  const isCustomerWish = reason === TireLeadReason.CUSTOMER_WISH;
  const foundOn = noddiFormatDate(lead.createdAt, CustomDateFormats.SHORT_WEEKDAY_DAY_MONTH_COMMA);
  const images = lead.images ?? [];
  const note = lead.tireDetails.noteToCustomer;
  const photoAlt = t`Photo of your tires`;

  return (
    <>
      <div className='flex items-center justify-between gap-3'>
        <span className='text-sm font-semibold text-muted-foreground'>
          {isCustomerWish ? t`You asked for an offer on ${foundOn}` : t`Found on ${foundOn}`}
        </span>
        <NoddiBadge variant={SEVERITY_BADGE_VARIANT[copy.severity]}>{getSeverityLabel(copy.severity)}</NoddiBadge>
      </div>
      {!isCustomerWish && (
        <ul className='flex flex-col divide-y divide-border'>
          {lead.tireDetails.positions.map((position) => (
            <li key={position.position} className='flex items-center justify-between gap-3 py-2.5 text-sm'>
              <span>{getTirePositionLabel(position.position)}</span>
              <span className='text-right font-semibold'>
                {position.reasons.map(getTireLeadReasonLabel).join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      {images.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-xs font-medium text-muted-foreground'>
            {/* eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term */}
            <Trans>Photos from the technician</Trans>
          </span>
          <div className='flex flex-wrap gap-2'>
            {images.map((image) => (
              <button
                key={image.imageUrl}
                type='button'
                onClick={() => setOpenPhotoUrl(image.imageUrl)}
                className='size-20 overflow-hidden rounded-lg bg-muted'
              >
                <img
                  src={image.imageUrl}
                  alt={image.position ? getTirePositionLabel(image.position) : photoAlt}
                  className='size-full object-cover'
                />
              </button>
            ))}
          </div>
        </div>
      )}
      {note && (
        <div className='flex flex-col gap-1 rounded-lg bg-muted p-4'>
          <span className='text-xs font-semibold text-muted-foreground'>
            {/* eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term */}
            <Trans>Note from the technician</Trans>
          </span>
          <span className='leading-relaxed'>{note}</span>
        </div>
      )}
      <NoddiDialog open={openPhotoUrl !== null} onClose={() => setOpenPhotoUrl(null)} title={photoAlt}>
        {openPhotoUrl && <img src={openPhotoUrl} alt={photoAlt} className='w-full rounded-lg' />}
      </NoddiDialog>
    </>
  );
};
